type SearchCancelToken = symbol;

interface LatestOrganizationSearchOptions<Result> {
    search: (
        organizationId: string,
        query: string,
        cancelToken: SearchCancelToken
    ) => Promise<Result>;
    abort: (cancelToken: SearchCancelToken) => void;
}

/** Keep at most one organization-content search request active per organization. */
export function createLatestOrganizationSearch<Result>({
    search,
    abort,
}: LatestOrganizationSearchOptions<Result>) {
    const activeSearches = new Map<string, SearchCancelToken>();

    async function request(organizationId: string, query: string): Promise<Result> {
        cancel(organizationId);

        const cancelToken = Symbol(`organization-search:${organizationId}`);
        activeSearches.set(organizationId, cancelToken);

        try {
            return await search(organizationId, query, cancelToken);
        } finally {
            if (activeSearches.get(organizationId) === cancelToken) {
                activeSearches.delete(organizationId);
            }
        }
    }

    function cancel(organizationId: string): void {
        const cancelToken = activeSearches.get(organizationId);
        if (!cancelToken) {
            return;
        }

        activeSearches.delete(organizationId);
        abort(cancelToken);
    }

    return { request, cancel };
}
